import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Slider,
  Grid,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { IAcabamento } from "../types/acabamento.types";
import {
  uploadFiguraAcabamento,
  definirNomeFiguraAcabamento,
  removerFiguraAcabamento,
  salvarCorAcabamento,
  removerCorAcabamento,
} from "../services/acabamentoService";
import { useNavigation } from "contexts/notificationsContext";
import { CampoNomeArquivo } from "../../components/CampoNomeArquivo";
import { useMidiaCompartilhada } from "../../hooks/useMidiaCompartilhada";
import { nomeArquivoPadrao } from "../../hooks/useNomeArquivoPadrao";

interface ModalGerenciamentoTexturaProps {
  open: boolean;
  onClose: () => void;
  acabamento: Partial<IAcabamento>;
  // Plano 21/09/2026 (Frente 3): `idAlvo` é o registro que recebeu a gravação — com o acabamento
  // Disponível, a API grava na cópia de trabalho e a página navega para ela.
  onSave: (updates: Partial<IAcabamento>, idAlvo: number) => void;
  onRefreshAcabamento: () => void;
}

type TabValue = "cor" | "figura";

// Helper para converter HEX para RGB
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

// Helper para converter RGB para HEX
const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
};

export const ModalGerenciamentoTextura: React.FC<ModalGerenciamentoTexturaProps> = ({
  open,
  onClose,
  acabamento,
  onSave,
  onRefreshAcabamento,
}) => {
  const { openNotification } = useNavigation();
  const [tabValue, setTabValue] = useState<TabValue>("cor");
  const [loading, setLoading] = useState(false);

  // Estado para cor
  const [corHex, setCorHex] = useState(acabamento.texturaCorHex || "#C13939");
  const [corRgb, setCorRgb] = useState(acabamento.texturaCorRgb || "");
  const [opacidade, setOpacidade] = useState(acabamento.texturaOpacidade || 100);
  const [formatoCor, setFormatoCor] = useState<"HEX" | "RGB">("HEX");

  // Estado para figura
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  // Etapas B/C/D (plano de mídias): o nome com que o arquivo é gravado — é ele que vincula
  // a figura aos acabamentos. Em cadastro NOVO sem nome informado, sugere <código>.PNG.
  const [nomeArquivo, setNomeArquivo] = useState<string>(
    nomeArquivoPadrao(acabamento.codigo, ".PNG", !acabamento.dataCriacao, acabamento.texturaFiguraNome)
  );

  // Etapa D: quantos OUTROS acabamentos usam este mesmo nome de figura.
  const produtosCompartilhando = useMidiaCompartilhada(
    open, "acabamentos", 2, nomeArquivo, acabamento.idTratamento
  );

  const [previewFigura, setPreviewFigura] = useState<string | null>(
    acabamento.texturaFiguraUrl || null
  );

  useEffect(() => {
    if (open) {
      // Resetar estados ao abrir
      setCorHex(acabamento.texturaCorHex || "#C13939");
      setCorRgb(acabamento.texturaCorRgb || "");
      setOpacidade(acabamento.texturaOpacidade || 100);
      setPreviewFigura(acabamento.texturaFiguraUrl || null);
      setArquivoSelecionado(null);
      // Etapa C: rearma o campo do nome a cada abertura, a partir do que está gravado.
      setNomeArquivo(
        nomeArquivoPadrao(acabamento.codigo, ".PNG", !acabamento.dataCriacao, acabamento.texturaFiguraNome)
      );
      setTabValue("cor");
    }
  }, [open, acabamento]);

  // Atualizar RGB quando HEX muda
  useEffect(() => {
    if (formatoCor === "HEX" && corHex) {
      const rgb = hexToRgb(corHex);
      if (rgb) {
        setCorRgb(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
      }
    }
  }, [corHex, formatoCor]);

  // Atualizar HEX quando RGB muda
  useEffect(() => {
    if (formatoCor === "RGB" && corRgb) {
      const match = corRgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        const r = parseInt(match[1], 10);
        const g = parseInt(match[2], 10);
        const b = parseInt(match[3], 10);
        setCorHex(rgbToHex(r, g, b));
      }
    }
  }, [corRgb, formatoCor]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setTabValue(newValue);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar extensão
    if (!file.name.toLowerCase().endsWith(".png")) {
      openNotification({
        message: "Formato não suportado. Utilize um arquivo .PNG",
        type: "error",
      });
      return;
    }

    // Validar tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      openNotification({
        message: "Arquivo muito grande. Tamanho máximo: 10MB",
        type: "error",
      });
      return;
    }

    setArquivoSelecionado(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewFigura(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoverFigura = async () => {
    if (!acabamento.idTratamento) return;

    setLoading(true);
    try {
      const retorno = await removerFiguraAcabamento(acabamento.idTratamento);
      if (retorno?.sucesso) {
        setPreviewFigura(null);
        setArquivoSelecionado(null);
        if (retorno.id === acabamento.idTratamento) onRefreshAcabamento();
        else onSave({ texturaFiguraUrl: undefined, texturaFiguraNome: undefined }, retorno.id);
        openNotification({
          message: "Figura removida com sucesso",
          type: "success",
        });
      }
    } catch (error) {
      console.error("Erro ao remover figura:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSalvar = async () => {
    if (!acabamento.idTratamento) {
      openNotification({
        message: "Salve o acabamento antes de definir a textura",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      if (tabValue === "cor") {
        // Salvar cor
        const retorno = await salvarCorAcabamento(
          acabamento.idTratamento,
          corHex,
          corRgb,
          opacidade
        );
        if (retorno?.sucesso) {
          onSave({
            texturaCorHex: corHex,
            texturaCorRgb: corRgb,
            texturaOpacidade: opacidade,
            texturaPrioridadeVisual: "cor",
          }, retorno.id);
          openNotification({
            message: "Cor salva com sucesso",
            type: "success",
          });
          if (retorno.id === acabamento.idTratamento) onRefreshAcabamento();
          onClose();
        }
      } else {
        // Reporte de 31/08 (item 6): sem arquivo escolhido, mas com o NOME informado, grava só
        // o nome — é como o acabamento assume uma figura que já está no acervo (F05A-P3b).
        if (!arquivoSelecionado && nomeArquivo?.trim() && nomeArquivo !== acabamento.texturaFiguraNome) {
          const resultado = await definirNomeFiguraAcabamento(acabamento.idTratamento, nomeArquivo);
          if (resultado) {
            const idAlvo = resultado.id ?? acabamento.idTratamento;
            onSave({
              texturaFiguraUrl: resultado.url,
              texturaFiguraNome: resultado.nomeArquivo,
              texturaPrioridadeVisual: "figura",
            }, idAlvo);
            openNotification(
              resultado.arquivoExiste
                ? { message: "Nome do arquivo salvo com sucesso", type: "success" }
                : {
                    message:
                      `Nome gravado, mas não há arquivo "${resultado.nomeArquivo}" no acervo da empresa. ` +
                      "A figura só aparecerá quando o arquivo for enviado.",
                    type: "warning",
                  }
            );
            if (idAlvo === acabamento.idTratamento) onRefreshAcabamento();
            onClose();
          }
          setLoading(false);
          return;
        }

        // Upload figura
        if (!arquivoSelecionado && !previewFigura) {
          openNotification({
            message: "Selecione uma figura PNG para fazer upload",
            type: "warning",
          });
          setLoading(false);
          return;
        }

        // Etapa D (F05A-P3b): figura compartilhada altera TODOS os acabamentos que a usam.
        if (arquivoSelecionado && produtosCompartilhando > 0) {
          const segue = window.confirm(
            `A figura "${nomeArquivo}" é usada por ${produtosCompartilhando} outro(s) acabamento(s). ` +
            `Substituí-la altera a textura de todos eles. Deseja continuar?`
          );
          if (!segue) {
            setLoading(false);
            return;
          }
        }

        if (arquivoSelecionado) {
          const retorno = await uploadFiguraAcabamento(acabamento.idTratamento, arquivoSelecionado, nomeArquivo);
          if (retorno) {
            onSave({
              texturaFiguraUrl: retorno.url,
              texturaFiguraNome: nomeArquivo || arquivoSelecionado.name,
              texturaPrioridadeVisual: "figura",
            }, retorno.id);
            openNotification({
              message: "Figura salva com sucesso",
              type: "success",
            });
            if (retorno.id === acabamento.idTratamento) onRefreshAcabamento();
            onClose();
          } else {
            // FASE 05 — 6ª validação/PO 2026-08-06: upload sem URL de retorno não pode passar em
            // silêncio (a tela ficava sem feedback e o usuário clicava de novo).
            openNotification({
              message: "Não foi possível enviar a figura. Tente novamente.",
              type: "error",
            });
          }
        }
      }
    } catch (error: any) {
      // FASE 05 — 6ª validação/PO 2026-08-06: exibir a falha real (antes só ia para o console e a
      // tela ficava muda mesmo com o upload não concluído).
      console.error("Erro ao salvar textura:", error);
      openNotification({
        message: error?.message || "Erro ao enviar a figura. Tente novamente.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const tituloModal = acabamento.codigo && acabamento.descricao
    ? `Acabamento – ${acabamento.codigo} – ${acabamento.descricao}`
    : "Acabamento – Textura";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxWidth: 800 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ color: "#4a4a4a", fontWeight: 600 }}>
            {tituloModal}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="RGB / HEX" value="cor" />
            <Tab label="FIGURA PNG" value="figura" />
          </Tabs>
        </Box>

        {tabValue === "cor" && (
          <Box>
            <Grid container spacing={3}>
              {/* Preview da cor */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    width: "100%",
                    height: 200,
                    backgroundColor: corHex,
                    opacity: opacidade / 100,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    backgroundImage: "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
                    backgroundSize: "20px 20px",
                    backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: corHex,
                      opacity: opacidade / 100,
                    }}
                  />
                </Paper>
              </Grid>

              {/* Formato e valor da cor */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    select
                    SelectProps={{
                      native: true,
                    }}
                    value={formatoCor}
                    onChange={(e) => setFormatoCor(e.target.value as "HEX" | "RGB")}
                    sx={{ minWidth: 100 }}
                  >
                    <option value="HEX">HEX</option>
                    <option value="RGB">RGB</option>
                  </TextField>
                  <TextField
                    fullWidth
                    label="Valor"
                    value={formatoCor === "HEX" ? corHex : corRgb}
                    onChange={(e) => {
                      if (formatoCor === "HEX") {
                        setCorHex(e.target.value);
                      } else {
                        setCorRgb(e.target.value);
                      }
                    }}
                    placeholder={formatoCor === "HEX" ? "#C13939" : "rgb(193, 57, 57)"}
                  />
                </Box>
              </Grid>

              {/* Opacidade */}
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography gutterBottom>
                    Opacidade: {opacidade}%
                  </Typography>
                  <Slider
                    value={opacidade}
                    onChange={(_, value) => setOpacidade(value as number)}
                    min={0}
                    max={100}
                    step={1}
                    valueLabelDisplay="auto"
                  />
                </Box>
              </Grid>

              {/* Botão remover cor */}
              {acabamento.texturaCorHex && (
                <Grid item xs={12}>
                  <Button
                    startIcon={<DeleteIcon />}
                    onClick={async () => {
                      if (acabamento.idTratamento) {
                        const retorno = await removerCorAcabamento(acabamento.idTratamento);
                        if (retorno?.sucesso) {
                          setCorHex("#C13939");
                          setCorRgb("");
                          setOpacidade(100);
                          if (retorno.id === acabamento.idTratamento) onRefreshAcabamento();
                          else onSave({ texturaCorHex: undefined, texturaCorRgb: undefined, texturaOpacidade: undefined }, retorno.id);
                          openNotification({
                            message: "Cor removida com sucesso",
                            type: "success",
                          });
                        }
                      }
                    }}
                    color="error"
                    variant="outlined"
                  >
                    Remover Cor
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {tabValue === "figura" && (
          <Box>
            <Grid container spacing={3}>
              {/* Área de upload */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "2px dashed #ccc",
                    borderRadius: 1,
                    p: 4,
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "#3C90AD",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  onClick={() => document.getElementById("file-input")?.click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept=".png"
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                  />
                  <CloudUploadIcon sx={{ fontSize: 48, color: "#999", mb: 2 }} />
                  <Typography variant="body1" gutterBottom>
                    Clique ou arraste um arquivo PNG aqui
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tamanho máximo: 10MB
                  </Typography>
                </Box>
              </Grid>

              {/* Reporte 02/09 (item A7): o campo do nome fica SEMPRE visível, como nos demais
                  produtos — é ele que permite gravar só o nome, sem subir arquivo novo (F2).
                  Antes ele morava dentro do preview e só aparecia depois de escolher um PNG. */}
              <Grid item xs={12}>
                <CampoNomeArquivo
                  valor={nomeArquivo}
                  onChange={setNomeArquivo}
                  extensoes=".PNG"
                  desabilitado={loading || !acabamento.idTratamento}
                  produtosCompartilhando={produtosCompartilhando}
                />
              </Grid>

              {/* Preview da figura */}
              {previewFigura && (
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      position: "relative",
                      p: 2,
                      border: "1px solid #ddd",
                      borderRadius: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={previewFigura}
                      alt="Preview"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 300,
                        display: "block",
                        margin: "0 auto",
                      }}
                    />
                    <Button
                      startIcon={<DeleteIcon />}
                      onClick={handleRemoverFigura}
                      color="error"
                      variant="outlined"
                      sx={{ mt: 2 }}
                      disabled={loading}
                    >
                      Remover Figura
                    </Button>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end", gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{
            minWidth: 120,
            borderColor: "#999",
            color: "#666",
            "&:hover": { borderColor: "#666", backgroundColor: "#f5f5f5" },
          }}
        >
          CANCELAR
        </Button>
        <Button
          onClick={handleSalvar}
          variant="contained"
          disabled={loading}
          sx={{
            minWidth: 120,
            backgroundColor: "#3C90AD",
            "&:hover": { backgroundColor: "#2d6f87" },
          }}
        >
          {loading ? "SALVANDO..." : "SALVAR"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
